import { z } from 'zod';
import { isoDate } from './date';
import { listInput } from './list';
import { JOB_STATES, jobMachine, type JobState } from '../statemachines/job';
import { PRICING_BASES } from '../pricing-basis';

/**
 * The idempotency envelope carried by every write (Foundation Rules 2–3): a
 * (clientId, mutationId) pair the server dedupes on via mutation_log. Optional at the API
 * (online writes just get free retry-safety); the offline queue fills it in later.
 */
export const idempotency = z.object({
  clientId: z.string().uuid().optional(),
  mutationId: z.string().uuid().optional(),
});
export type Idempotency = z.infer<typeof idempotency>;

const optionalText = (max: number) =>
  z.string().trim().max(max).optional().or(z.literal(''));

// ── Contacts ─────────────────────────────────────────────────────────────────
export const contactCreateInput = idempotency.extend({
  customerId: z.string().uuid(),
  firstName: z.string().trim().min(1).max(100),
  lastName: optionalText(100),
  email: z.string().trim().email().max(200).optional().or(z.literal('')),
  phone: optionalText(40),
  roleLabel: optionalText(60),
  isPrimary: z.boolean().optional(),
});
export type ContactCreateInput = z.infer<typeof contactCreateInput>;

// ── Properties ───────────────────────────────────────────────────────────────
export const propertyCreateInput = idempotency.extend({
  customerId: z.string().uuid(),
  label: z.string().trim().min(1).max(120),
  address1: optionalText(200),
  address2: optionalText(200),
  city: optionalText(120),
  state: z.string().trim().length(2).toUpperCase().optional().or(z.literal('')),
  zip: optionalText(12),
  siteContactId: z.string().uuid().optional(),
  accessNotes: optionalText(2000),
  gateCode: optionalText(60),
});
export type PropertyCreateInput = z.infer<typeof propertyCreateInput>;

// ── Visits ───────────────────────────────────────────────────────────────────
export const visitWindow = z.enum(['exact', 'am', 'pm', 'all_day']);
export type VisitWindow = z.infer<typeof visitWindow>;

export const scheduleOverrideInput = z
  .object({ reason: z.string().trim().min(1).max(500) })
  .strict();
export type ScheduleOverrideInput = z.infer<typeof scheduleOverrideInput>;

const visitFields = {
  scheduledStartAt: z.coerce.date(),
  scheduledEndAt: z.coerce.date().optional(),
  window: visitWindow.optional(),
  instructions: optionalText(2000),
  assigneeUserIds: z.array(z.string().uuid()).max(10).optional(),
  leadUserId: z.string().uuid().optional(),
  scheduleOverride: scheduleOverrideInput.optional(),
};

export const visitCreateInput = idempotency.extend({
  jobId: z.string().uuid(),
  ...visitFields,
});
export type VisitCreateInput = z.infer<typeof visitCreateInput>;

export const visitRescheduleInput = idempotency.extend({
  visitId: z.string().uuid(),
  scheduledStartAt: z.coerce.date(),
  scheduledEndAt: z.coerce.date().optional(),
  scheduleOverride: scheduleOverrideInput.optional(),
});
export type VisitRescheduleInput = z.infer<typeof visitRescheduleInput>;

/**
 * Who is on this visit — the gesture the dispatch board exists for (114).
 *
 * ⚠️ The WHOLE SET, not a delta. `assigneeUserIds` is the complete list of who should be on
 * the visit when this returns; anyone currently on it and absent from the list comes off.
 * A pair of add/remove mutations would let a board that dropped one request end up in a
 * state neither the office nor the server intended, and there would be no single moment
 * that is "the answer". Sending the set makes the write idempotent by construction: replay
 * it and you get the same visit.
 *
 * ⚠️ An EMPTY array is meaningful and legal — it is "take everyone off", which is how a
 * visit returns to the Unassigned lane. It is not the same as omitting the field, which the
 * schema does not permit: there is no way to say "leave them alone" here, because that is
 * what not calling this is for.
 *
 * `max(10)` matches `visitFields.assigneeUserIds` — one cap, not a second opinion about how
 * many people fit in a van.
 */
export const visitSetAssigneesInput = idempotency.extend({
  visitId: z.string().uuid(),
  assigneeUserIds: z.array(z.string().uuid()).max(10),
  leadUserId: z.string().uuid().optional(),
  scheduleOverride: scheduleOverrideInput.optional(),
});
export type VisitSetAssigneesInput = z.infer<typeof visitSetAssigneesInput>;

/**
 * The dispatch board's LANES — one per technician, for one day (114 part 2).
 *
 * ⚠️ A calendar DATE, not an instant, and not a range. A lane's van comes from an
 * effective-dated assignment (`asset_assignments.starts_on` / `ends_on`), which is a
 * question about a DAY in the shop's zone — "who was in Van 3 on Thursday". An instant
 * would carry the caller's timezone into that answer, and a range has no single answer to
 * give: a tech can change vans mid-week.
 */
export const boardRigsInput = z.object({ date: isoDate }).strict();
export type BoardRigsInput = z.infer<typeof boardRigsInput>;

/**
 * One lane: the technician, and the van travelling with them on that date.
 *
 * ⭐ Shipped as a TYPE rather than left to inference, for the reason 107's `DueRow` taught
 * this repo: the web cannot import a type out of `apps/api`, so it restates the row in the
 * component, and a restated shape drifts the first time a field is added on one side.
 */
export interface BoardRig {
  userId: string;
  name: string | null;
  email: string | null;
  role: string;
  /**
   * Null is a real, common answer, not an error — a tech who drives no van still gets a
   * lane, because their work still has to be visible. The board renders the absence.
   */
  vehicle: { assetId: string; name: string; plate: string | null } | null;
}

/**
 * ⚠️ A CAP, because this read resolves a van per person per day in memory. The week board
 * asks for 7; a month view would ask for 31. Anything past that is not a board, and an
 * uncapped range is a query whose cost the caller chooses.
 */
export const BOARD_RIGS_RANGE_MAX_DAYS = 31;

/** Whole days from `a` to `b`, counting BOTH ends — 2026-08-24→2026-08-30 is 7. */
function daysInclusive(a: string, b: string): number {
  const ms = Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`);
  return Math.round(ms / 86_400_000) + 1;
}

/**
 * The lanes for a RANGE of days — the week board's roster (121).
 *
 * 🔴 **A separate input, and the single-date one above is deliberately left alone.**
 * `boardRigsInput` is `.strict()` and the day board is its only caller; widening it would
 * make every existing caller pay for a shape it does not want, and `.strict()` exists
 * precisely so a stray field is refused rather than ignored.
 *
 * ⚠️ Inclusive of BOTH ends, unlike `DateRange`'s half-open `[from, to)`. These are calendar
 * DAYS a person picked off a grid, not instants: "Mon to Sun" means seven columns, and a
 * half-open range would silently drop Sunday. The difference is stated because the two live
 * side by side in this file.
 */
export const boardRigsRangeInput = z
  .object({ from: isoDate, to: isoDate })
  .strict()
  .refine((v) => v.from <= v.to, {
    message: 'The range starts after it ends',
    path: ['to'],
  })
  .refine((v) => daysInclusive(v.from, v.to) <= BOARD_RIGS_RANGE_MAX_DAYS, {
    message: `A range may cover at most ${BOARD_RIGS_RANGE_MAX_DAYS} days`,
    path: ['to'],
  });
export type BoardRigsRangeInput = z.infer<typeof boardRigsRangeInput>;

/**
 * One lane over a range: the technician, and which van travelled with them on WHICH day.
 *
 * 🔴 **`vehicleByDay`, not one `vehicle`.** The van is a per-DAY fact — that is the whole
 * reason `boardRigs` exists rather than a roster of members — so a week lane cannot name one
 * van honestly. A tech who changes vans on Wednesday would otherwise have the lane claim the
 * wrong one for four days, on the exact screen a dispatcher opens to check where people are.
 *
 * ⭐ So the LANE is the technician and the VAN belongs to the cell (121's ruling). Keys are
 * `YYYY-MM-DD`; a day with no van is simply absent rather than mapped to null, so a caller
 * cannot tell "no van" from "day not in range" by accident — `vanless` answers the question
 * the lane header actually asks.
 */
export interface BoardRigRange {
  userId: string;
  name: string | null;
  email: string | null;
  role: string;
  vehicleByDay: Record<string, { assetId: string; name: string; plate: string | null }>;
  /**
   * True when this tech had no van on ANY day in view — decision 8's "no van assigned" warn.
   *
   * ⚠️ Computed here rather than left to the component, because "no van all week" and "no van
   * on Thursday" are different facts and only the first belongs in a lane header. A tech
   * vanless on Thursday alone is a Thursday problem, and the day panel is where Thursday is
   * described.
   */
  vanless: boolean;
}

/**
 * How many visits one move may carry. A rained-out day at a fifteen-tech shop is the case
 * this is sized for; past that it stops being a dispatch decision and starts being an import.
 */
export const BULK_RESCHEDULE_MAX = 100;

/**
 * Moving several visits at once — the rain-day mutation.
 *
 * A whole-day SHIFT, not a target instant, because each visit keeps its own time: the 8 AM
 * and the 2 PM both slide two days and stay 8 AM and 2 PM. Sending an integer also keeps the
 * client out of timezone arithmetic entirely — the server resolves the wall clock in the
 * shop's zone, which is the only place that knows it.
 */
export const visitRescheduleManyInput = idempotency
  .extend({
    visitIds: z.array(z.string().uuid()).min(1).max(BULK_RESCHEDULE_MAX),
    shiftDays: z.number().int().min(-365).max(365),
    scheduleOverride: scheduleOverrideInput.optional(),
  })
  .refine((v) => v.shiftDays !== 0, {
    message: 'Pick a different day to move these to',
    path: ['shiftDays'],
  });
export type VisitRescheduleManyInput = z.infer<typeof visitRescheduleManyInput>;

/** Simple visit transitions: enRoute / start / complete / cancel / abort. */
export const visitTransitionInput = idempotency.extend({
  visitId: z.string().uuid(),
});
export type VisitTransitionInput = z.infer<typeof visitTransitionInput>;

/**
 * Arrival proof — the ONE fix a tech's device may report, at Start (119, SPEC N1a).
 *
 * 🔴 **Switched off by ADR 0001 (no device location).** The server validates it and stores
 * nothing: an app version cached before the change can still send one in a Start queued
 * offline, and that Start must land. It stays in `visitStartInput` so its bounds still refuse
 * a malformed payload.
 *
 * ⚠️ **OPTIONAL, and that is the whole ruling.** Ref 23411385's prompt says *"If not, you
 * can't start the job"*; N1a refuses that half. A tech who declines the permission, or who
 * is in a basement with no signal, still starts — the visit simply records no fix. A gate
 * that hard-blocks Start turns a phone permission into a condition of employment, and it
 * breaks the field app exactly where `TechShell`'s four offline subsystems exist because
 * signal fails.
 *
 * ⚠️ Bounds are enforced HERE as well as by the DB CHECK, so a bad reading comes back as a
 * sentence rather than a 500 — the `isRealDate` precedent (review 106 m1): the shape and the
 * value are both this layer's job.
 */
export const visitArrivalFix = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  /**
   * Metres of uncertainty the device reported. Capped rather than unbounded: a reading with
   * kilometres of error is not arrival proof, and storing it as though it were would make
   * the record look stronger than it is.
   */
  accuracyM: z.number().int().min(0).max(100_000).optional(),
});
export type VisitArrivalFix = z.infer<typeof visitArrivalFix>;

export const visitStartInput = visitTransitionInput.extend({
  arrival: visitArrivalFix.optional(),
});
export type VisitStartInput = z.infer<typeof visitStartInput>;

// ── Jobs ─────────────────────────────────────────────────────────────────────
export const jobPriority = z.enum(['normal', 'urgent']);

/** Said when a new job arrives with no service type: missing, blank or not an id at all. */
const PICK_A_SERVICE_TYPE = 'Pick a service type for this job';

/**
 * A NEW job's service type, REQUIRED (ops insights Phase 1): every new job is classified from
 * the start. One field for every input that creates a job (the new-job sheet, scheduling a
 * quote and approving a request), so each path refuses a missing type in the same words. The
 * server also checks it is one of this shop's types and that it is in use
 * (`lockServiceTypeForNewJob`); this only says one was sent.
 *
 * A quote or a request carries no type of its own, so a conversion is handed the one the office
 * picks as it converts (`toJobFields`, and the web's `ServiceTypeStep`).
 */
export const newJobServiceTypeId = z
  .string({ error: PICK_A_SERVICE_TYPE })
  .uuid({ error: PICK_A_SERVICE_TYPE });

/**
 * What a conversion asks of the job it creates (scheduling a quote, `quoteToJobInput`, and
 * approving a request, `requestApproveToJobInput`): the service type the office picks, and
 * optionally the first visit's start and crew. One set, as `visitFields` is for a visit, so the
 * two conversions cannot drift apart; the crew's cap is the visit's.
 */
export const toJobFields = {
  serviceTypeId: newJobServiceTypeId,
  scheduledStartAt: z.coerce.date().optional(),
  scheduledEndAt: z.coerce.date().optional(),
  assigneeUserIds: visitFields.assigneeUserIds,
  scheduleOverride: visitFields.scheduleOverride,
};

export const jobCreateInput = idempotency.extend({
  customerId: z.string().uuid(),
  propertyId: z.string().uuid(),
  serviceTypeId: newJobServiceTypeId,
  title: z.string().trim().min(1).max(200),
  description: optionalText(4000),
  priority: jobPriority.optional(),
  // Optional first visit — creating a job with a scheduled visit is the common dispatch
  // flow (moves the job straight to 'scheduled').
  visit: z.object(visitFields).optional(),
});
export type JobCreateInput = z.infer<typeof jobCreateInput>;

export const jobTransitionInput = idempotency.extend({
  jobId: z.string().uuid(),
  reason: optionalText(500),
});
export type JobTransitionInput = z.infer<typeof jobTransitionInput>;

/**
 * The Jobs list's "No service type" (ops insights 08): the filter value for the jobs that have
 * none, the older jobs the office classifies. ONLY ever a filter value: no input sets a job to
 * it (`jobSetServiceTypeInput`).
 */
export const NO_SERVICE_TYPE = 'none';

/**
 * A value of the Jobs list's service type filter: one of the shop's types by id, or
 * `NO_SERVICE_TYPE`. One definition for the list's input, its address (`/jobs?serviceType=`,
 * which the Revenue page's "Classify these jobs" opens) and the Revenue page's drill.
 */
export const serviceTypeFilterValue = z.union([
  z.literal(NO_SERVICE_TYPE),
  z.string().uuid(),
]);

/**
 * The Jobs list's input: the list contract, and one narrowing that is not a column's text
 * filter. `serviceType` is one of the shop's types by id, or `NO_SERVICE_TYPE`; absent means
 * every job. The server reads it against the type the list SHOWS (`listJobs`), so a job the
 * list draws with the dash is a job "No service type" finds. `.strict()`, like the register's
 * (`assetListInput`): a stray field is refused, not ignored.
 */
export const jobListInput = listInput
  .extend({
    serviceType: serviceTypeFilterValue.optional(),
    /**
     * One customer's jobs: the New ticket sheet's job picker (ops insights 5a ticket 04). It
     * narrows; it does not scope. `job.list` needs `job:browse`, which a tech never holds (CRM 006).
     */
    customerId: z.string().uuid().optional(),
  })
  .strict();
export type JobListInput = z.infer<typeof jobListInput>;

/**
 * How many jobs one "Set service type" may carry: a Jobs list page is 50 rows, so a whole page
 * fits twice over. The rain-day move's cap (`BULK_RESCHEDULE_MAX`) is the precedent.
 */
export const SET_SERVICE_TYPE_MAX = 100;

/** Said when a set arrives with no type: missing, null, blank or not an id at all. */
const PICK_ONE_TO_SET =
  "Pick the service type to set. A job's service type can be changed, but never cleared.";

/**
 * Give jobs a service type (ops insights 08): its own action rather than a field of
 * `job.update`. The type is REQUIRED and never null, and "none" is not a type (it is not an id):
 * nothing in the product can clear a job's type. Why, and the rest of the rules:
 * `setJobServiceType` in apps/api.
 */
export const jobSetServiceTypeInput = idempotency
  .extend({
    jobIds: z.array(z.string().uuid()).min(1).max(SET_SERVICE_TYPE_MAX),
    serviceTypeId: z.string({ error: PICK_ONE_TO_SET }).uuid({ error: PICK_ONE_TO_SET }),
  })
  .strict();
export type JobSetServiceTypeInput = z.infer<typeof jobSetServiceTypeInput>;

/**
 * Change how one job bills its parts (Inventory v2 Phase 1). The office's own act, refused on a
 * job from an accepted quote and on an agreement visit, which are always Fixed:
 * `setJobPricingBasis` in apps/api.
 */
export const jobSetPricingBasisInput = idempotency
  .extend({
    jobId: z.string().uuid(),
    basis: z.enum(PRICING_BASES),
  })
  .strict();
export type JobSetPricingBasisInput = z.infer<typeof jobSetPricingBasisInput>;

/** A job a set left as it was, and why: named, never silently dropped. */
export interface JobServiceTypeSkip {
  jobId: string;
  /** Null when there is no job of this shop's to name (an id the set could not find). */
  jobNumber: number | null;
  title: string | null;
  why: string;
}

/**
 * What a set did, partial by design: the jobs it changed and the ones it skipped, in separate
 * fields a screen has to render both of (the rain-day move's contract). A TYPE here rather than
 * left to inference, for the reason `BoardRig` gives: the web cannot import one out of apps/api.
 */
export interface JobSetServiceTypeResult {
  /** The type the jobs were given, by the name it had at that moment. */
  serviceType: { id: string; label: string };
  changed: { jobId: string; jobNumber: number }[];
  skipped: JobServiceTypeSkip[];
}

/**
 * The OPEN job states — every state except the two the machine calls terminal (117).
 *
 * ⭐ DERIVED from `jobMachine.terminal`, never listed by hand. A ninth job state added to
 * `JOB_STATES` appears in this rail automatically; a hand-written list would leave it out
 * silently, and a status rail that omits a status is a denominator that lies by omission.
 *
 * ⚠️ `invoiced` IS open. The work is done and billed, but nobody has closed the job — it is
 * a state an owner still has to act on, and dropping it would hide the pile of jobs waiting
 * on a close.
 */
export const JOB_OPEN_STATES = JOB_STATES.filter(
  (s) => !(jobMachine.terminal as readonly string[]).includes(s),
);

/**
 * Counts of OPEN jobs by status, for `/today`'s rail.
 *
 * ⚠️ `excluded` rides on the payload so the page can NAME what it left out instead of
 * printing a bare set of numbers. An unstated denominator is how a percentage lies, and the
 * rail's total is meaningless to a reader who does not know that closed and canceled jobs
 * are not in it.
 */
export type JobStatusCounts = {
  counts: { status: JobState; count: number }[];
  /** The sum of `counts` — the rail's denominator, stated rather than re-derived. */
  total: number;
  excluded: readonly JobState[];
};

// ── Notes ────────────────────────────────────────────────────────────────────
export const noteEntityType = z.enum(['customer', 'property', 'job', 'visit']);
export const noteAppendInput = idempotency.extend({
  entityType: noteEntityType,
  entityId: z.string().uuid(),
  body: z.string().trim().min(1).max(4000),
  pinned: z.boolean().optional(),
});
export type NoteAppendInput = z.infer<typeof noteAppendInput>;

/** Removing an attachment (PRD v2 §5). Soft-delete; the file itself is kept. */
export const attachmentDeleteInput = idempotency.extend({
  attachmentId: z.string().uuid(),
});
export type AttachmentDeleteInput = z.infer<typeof attachmentDeleteInput>;
